import { FC, HTMLAttributes } from 'react';
import tw, { css, styled } from 'twin.macro';
import 'styled-components/macro';

export type BaseBadgeProps = HTMLAttributes<HTMLDivElement>;

export const BaseBadge: FC<BaseBadgeProps> = styled.div`
  ${tw`inline-flex items-center flex-nowrap outline-none px-2 py-0 rounded-full font-semibold whitespace-nowrap text-xs/loose transition-all duration-fast ease-in-out`}
`;

export interface BadgeProps extends BaseBadgeProps {
  onAction?: BaseBadgeProps['onClick'] & BaseBadgeProps['onKeyDown'];
}

/**
 * Note: shows icon for active and hover state if "onAction" handler provided
 */
const ActionBadge: FC<BadgeProps> = ({ onAction, children, ...props }) => (
  <BaseBadge
    onClick={onAction}
    onKeyDown={
      onAction
        ? e => {
            return ['Enter', ' '].includes(e.key) && onAction(e);
          }
        : undefined
    }
    role={onAction ? 'button' : undefined}
    tabIndex={onAction ? 0 : undefined}
    css={onAction && tw`cursor-pointer`}
    {...props}
  >
    {children}
  </BaseBadge>
);

export const FillBadge = styled(ActionBadge)`
  ${tw`bg-gray-70 text-white`}
  ${({ onAction }) =>
    onAction &&
    css`
      :hover {
        ${tw`bg-primary`}
      }
      :focus,
      :active {
        ${tw`bg-primary-dark`}
      }
    `}
`;

export const OutlineBadge = styled(ActionBadge)`
  ${tw`shadow-sm shadow-gray-15`}
  ${tw`bg-white text-gray-50`}
  ${({ onAction }) =>
    onAction &&
    css`
      ${tw`shadow-sm shadow-gray-15`}
      :hover {
        ${tw`text-primary`}
        box-shadow: 0 0 0 1px currentColor inset;
      }
      :focus,
      :active {
        ${tw`text-primary-dark`}
        box-shadow: 0 0 0 1px currentColor inset;
      }
    `}
`;

export const TextBadge = styled(ActionBadge)`
  ${tw`text-gray-70 px-0`}
`;
